import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  LogOut,
  Lock,
  Mail,
  Eye,
  EyeOff,
  ShoppingBag,
  Package,
  TrendingUp,
  Calendar,
  AlertCircle,
  Download,
  RefreshCw,
  Filter,
  ChevronRight,
  X,
  Phone,
  MapPin,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  Truck,
  Search,
} from 'lucide-react';
import { AdminAuthProvider, useAdminAuth } from '../context/AdminAuthContext';
import { formatPrice } from '../lib/format';
import AdminProducts from './AdminProducts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Login screen ---
const AdminLogin = () => {
  const { login } = useAdminAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(form.username, form.password);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Eroare la autentificare');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-neutral-200 p-8">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-neutral-900 text-white items-center justify-center mb-3">
            <Lock className="w-7 h-7" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-neutral-900">Panou Administrare</h1>
          <p className="text-sm text-neutral-500 mt-1">Acces restricționat — doar pentru administratori</p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 flex items-start gap-2 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                required
                type="email"
                autoComplete="username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Parolă</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                required
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-neutral-900 hover:bg-neutral-800 disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-colors"
          >
            {busy ? 'Se conectează...' : 'Conectare'}
          </button>
        </form>

        <Link to="/" className="block text-center text-xs text-neutral-500 hover:text-neutral-900 mt-6">
          ← Înapoi la magazin
        </Link>
      </div>
    </div>
  );
};

// --- Stats card ---
const StatCard = ({ icon: Icon, label, value, accent = 'amber' }) => {
  const colors = {
    amber: 'bg-amber-50 text-amber-700',
    green: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-500 font-medium">{label}</div>
          <div className="mt-2 text-3xl font-serif font-bold text-neutral-900">{value}</div>
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[accent]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const STATUS_META = {
  pending_awb: { label: 'În așteptare AWB', color: 'bg-amber-100 text-amber-800', icon: Clock },
  awb_created: { label: 'AWB generat', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  awb_failed: { label: 'AWB eșuat', color: 'bg-red-100 text-red-800', icon: XCircle },
  received: { label: 'Primită', color: 'bg-blue-100 text-blue-800', icon: ShoppingBag },
  shipped: { label: 'Expediată', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  delivered: { label: 'Livrată', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  cancelled: { label: 'Anulată', color: 'bg-neutral-200 text-neutral-700', icon: XCircle },
};

// --- Order detail modal ---
const OrderDetailModal = ({ orderId, onClose, onUpdated }) => {
  const { authHeaders } = useAdminAuth();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    axios.get(`${API}/admin/orders/${orderId}`, { headers: authHeaders() })
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null));
  }, [orderId]);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        { headers: authHeaders() }
      );
      setOrder({ ...order, status });
      onUpdated?.();
    } finally {
      setBusy(false);
    }
  };

  const downloadAwbPdf = () => {
    if (!order?.awb_number) return;
    window.open(`${API}/orders/${order.id}/awb-pdf`, '_blank');
  };

  if (!orderId) return null;

  return (
    <div className="fixed inset-0 z-[100] flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="ml-auto relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-serif font-bold text-xl">Detalii comandă</h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        {!order ? (
          <div className="p-10 text-center text-neutral-500">Se încarcă...</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider">Comandă</div>
                <div className="font-mono font-bold text-lg">{order.order_number}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  {new Date(order.created_at).toLocaleString('ro-RO')}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-neutral-500 uppercase tracking-wider">Total ramburs</div>
                <div className="font-serif font-bold text-2xl">{formatPrice(order.total)}</div>
              </div>
            </div>

            {/* AWB block */}
            {order.awb_number ? (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-amber-800 uppercase tracking-wider font-medium">AWB Sameday</div>
                    <div className="font-mono font-bold text-lg text-neutral-900">{order.awb_number}</div>
                    {order.shipping_cost && (
                      <div className="text-xs text-neutral-600 mt-1">
                        Cost expediere: {formatPrice(order.shipping_cost)}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={downloadAwbPdf}
                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-full"
                  >
                    <Download className="w-4 h-4" /> PDF
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800">
                AWB nu a fost generat. {order.awb_error && (
                  <div className="mt-1 text-xs font-mono break-all">{order.awb_error}</div>
                )}
              </div>
            )}

            {/* Status */}
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Status</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_META).map(([key, meta]) => (
                  <button
                    key={key}
                    disabled={busy}
                    onClick={() => updateStatus(key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      order.status === key
                        ? `${meta.color} border-current`
                        : 'bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <meta.icon className="w-3.5 h-3.5" />
                    {meta.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Customer */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Client</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-5 text-neutral-400">@</span>
                  <span className="font-medium">{order.customer?.name}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-neutral-400" />
                  <a href={`mailto:${order.customer?.email}`} className="text-amber-700 hover:underline">
                    {order.customer?.email}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-neutral-400" />
                  <a href={`tel:${order.customer?.phone}`} className="text-amber-700 hover:underline font-medium">
                    {order.customer?.phone}
                  </a>
                </li>
              </ul>
            </section>

            {/* Delivery */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Livrare</h3>
              <div className="bg-neutral-50 rounded-xl p-4 text-sm space-y-1.5">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-neutral-500" />
                  <span className="font-medium">
                    {order.delivery?.method === 'locker' ? 'Easybox Locker' : 'La domiciliu'}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-neutral-500 mt-0.5" />
                  <div>
                    <div>{order.delivery?.address}</div>
                    <div className="text-neutral-600">
                      {order.delivery?.city}, {order.delivery?.county}
                      {order.delivery?.postal_code ? ` · ${order.delivery.postal_code}` : ''}
                    </div>
                    {order.delivery?.locker_id && (
                      <div className="text-xs text-neutral-500 mt-1">Locker ID: {order.delivery.locker_id}</div>
                    )}
                  </div>
                </div>
              </div>
              {order.notes && (
                <div className="mt-3 text-xs text-neutral-600 bg-amber-50 rounded-lg p-3">
                  <strong>Note:</strong> {order.notes}
                </div>
              )}
            </section>

            {/* Items */}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Produse</h3>
              <div className="border border-neutral-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wider text-neutral-500">Produs</th>
                      <th className="text-center p-3 font-medium text-xs uppercase tracking-wider text-neutral-500">Cant.</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wider text-neutral-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, i) => (
                      <tr key={i} className="border-t border-neutral-100">
                        <td className="p-3">{item.name}</td>
                        <td className="p-3 text-center">{item.qty}</td>
                        <td className="p-3 text-right font-medium">{formatPrice(item.price * item.qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><dt className="text-neutral-500">Subtotal</dt><dd>{formatPrice(order.subtotal)}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500">Livrare</dt><dd>{order.shipping ? formatPrice(order.shipping) : 'Gratuit'}</dd></div>
                <div className="flex justify-between pt-2 border-t border-neutral-100 text-base font-bold">
                  <dt>Total ramburs</dt><dd>{formatPrice(order.total)}</dd>
                </div>
              </dl>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
};

// --- Main dashboard ---
const Dashboard = () => {
  const { user, logout, authHeaders } = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('orders');

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/orders`, { headers: authHeaders(), params: filter ? { status: filter } : {} }),
      axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
    ])
      .then(([o, s]) => {
        setOrders(o.data);
        setStats(s.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.awb_number?.toLowerCase().includes(q) ||
      o.customer?.name?.toLowerCase().includes(q) ||
      o.customer?.email?.toLowerCase().includes(q) ||
      o.customer?.phone?.includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-serif text-2xl font-bold text-neutral-900">AXA</Link>
            <span className="text-xs uppercase tracking-wider text-neutral-500 border-l border-neutral-200 pl-3">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
              title="Reîmprospătează"
            >
              <RefreshCw className={`w-4 h-4 text-neutral-700 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="hidden sm:flex flex-col text-right leading-tight">
              <span className="text-xs text-neutral-500">Conectat ca</span>
              <span className="text-sm font-medium text-neutral-900">{user?.username}</span>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm text-neutral-700 hover:text-red-600 px-3 py-2 hover:bg-red-50 rounded-full transition-colors"
            >
              <LogOut className="w-4 h-4" /> Deconectare
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-white border border-neutral-200 rounded-full p-1 w-fit">
          <button
            onClick={() => setTab('orders')}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === 'orders' ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <ShoppingBag className="w-4 h-4" /> Comenzi
          </button>
          <button
            onClick={() => setTab('products')}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === 'products' ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Package className="w-4 h-4" /> Produse
          </button>
        </div>

        {tab === 'products' ? <AdminProducts /> : (<>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={ShoppingBag} label="Total comenzi" value={stats.total_orders} accent="amber" />
            <StatCard icon={Calendar} label="Astăzi" value={stats.orders_today} accent="blue" />
            <StatCard icon={Package} label="Cu AWB" value={stats.orders_with_awb} accent="green" />
            <StatCard
              icon={TrendingUp}
              label="Vânzări totale"
              value={formatPrice(stats.total_revenue)}
              accent="amber"
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Caută după nr. comandă, AWB, nume, email, telefon..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-neutral-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-neutral-300 focus:border-amber-500 outline-none rounded-lg px-3 py-2.5 text-sm bg-white cursor-pointer"
            >
              <option value="">Toate statusurile</option>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Orders table */}
        <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-neutral-500">Se încarcă comenzile...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-neutral-500">
              <ShoppingBag className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
              Nu există comenzi pentru filtrele selectate.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-500 font-medium">Comandă</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-500 font-medium">Client</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-500 font-medium hidden md:table-cell">Livrare</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-neutral-500 font-medium">Status</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-neutral-500 font-medium">Total</th>
                    <th className="text-center p-3 text-xs uppercase tracking-wider text-neutral-500 font-medium">AWB</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const meta = STATUS_META[o.status] || STATUS_META.received;
                    return (
                      <tr
                        key={o.id}
                        onClick={() => setSelectedId(o.id)}
                        className="border-t border-neutral-100 hover:bg-amber-50/30 cursor-pointer transition-colors"
                      >
                        <td className="p-3">
                          <div className="font-mono text-xs font-semibold text-neutral-900">{o.order_number}</div>
                          <div className="text-[11px] text-neutral-500 mt-0.5">
                            {new Date(o.created_at).toLocaleString('ro-RO', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-neutral-900">{o.customer?.name}</div>
                          <div className="text-xs text-neutral-500">{o.customer?.phone}</div>
                        </td>
                        <td className="p-3 hidden md:table-cell">
                          <div className="text-xs text-neutral-700">{o.delivery?.city}</div>
                          <div className="text-[11px] text-neutral-500">{o.delivery?.county}</div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${meta.color}`}>
                            <meta.icon className="w-3 h-3" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="p-3 text-right font-semibold text-neutral-900">
                          {formatPrice(o.total)}
                        </td>
                        <td className="p-3 text-center">
                          {o.awb_number ? (
                            <a
                              href={`${API}/orders/${o.id}/awb-pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-mono"
                            >
                              <Download className="w-3 h-3" />
                              {o.awb_number}
                            </a>
                          ) : (
                            <span className="text-xs text-neutral-400">—</span>
                          )}
                        </td>
                        <td className="p-3 text-neutral-400">
                          <ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>)}
      </main>

      <OrderDetailModal
        orderId={selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={load}
      />
    </div>
  );
};

// --- Outer page ---
const AdminInner = () => {
  const { user, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-500 text-sm">Se încarcă...</div>
      </div>
    );
  }
  return user ? <Dashboard /> : <AdminLogin />;
};

const Admin = () => (
  <AdminAuthProvider>
    <AdminInner />
  </AdminAuthProvider>
);

export default Admin;
